import React from 'react';
import { FieldType } from 'types/FieldTypes';
import FormModal from 'components/modals/FormModal';
import { useSettingsDivision } from 'components/settings/SettingsDivisionContext';
import { useSubcontractorConversion } from 'services/subcontractors/useSubcontractorConversion';
import DropdownButton from 'components/buttons/DropdownButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsis } from '@fortawesome/free-solid-svg-icons';

// The subcontractor form is the user form without a role field, the
// fixed subcontractor role is assigned by the backend, plus a company name
const SubcontractorForm: React.FC<{ viewKey?: string, itemId?: number }> = ({ viewKey, itemId }) => {

    // Existing subcontractors can be converted into a regular user
    const { convertSubcontractorToUser } = useSubcontractorConversion();

    // Prefill the division with the division selected in the settings menu
    const { selectedDivision, isEnvironmentSelected } = useSettingsDivision();
    const divisionDefaultValue = !isEnvironmentSelected && selectedDivision ? { value: String(selectedDivision.id) } : undefined;

    const apiObject = 'subcontractor'

    // Plug in the fields and their properties:
    const fields: FieldType[] = [
        {
            name: 'first_name',
            label: 'user.general.first_name_label',
            type: 'text',
            placeholder: 'user.general.first_name_placeholder',
            width: '50%',
            group: 'field-row',
        },
        {
            name: 'last_name',
            label: 'user.general.last_name_label',
            type: 'text',
            placeholder: 'user.general.last_name_placeholder',
            width: '50%',
            group: 'field-row',
        },
        {
            name: 'company_name',
            label: 'subcontractor.general.company_name_label',
            type: 'text',
            placeholder: 'subcontractor.general.company_name_placeholder',
        },
        {
            name: 'email',
            label: 'user.general.email_label',
            type: 'email',
            placeholder: 'user.general.email_placeholder',
            helperText: 'subcontractor.general.email_helperText',
        },
        {
            name: 'password',
            label: 'user.general.password_label',
            type: 'password',
            placeholder: 'user.general.password_placeholder',
        },
        {
            name: 'division',
            defaultValue: divisionDefaultValue,
            label: 'user.general.division_label',
            type: 'dropdown',
            disabled_selected: 'user.general.select_division_disabled_selected',
            showSearch: false,
            apiRequest: [{
                endpoint: 'get_division_list',
                object: 'division',
                params: { is_active: true },
                useCache: true
            }],
            allowNoneOption: true,
            activeFeature: 'divisions',
            helperText: 'user.general.division_helperText'
        },
        {
            name: 'profile_picture',
            label: 'user.general.profile_picture_label',
            type: 'image',
            imageType: 'smallCircle',
            dropzoneText: 'user.general.profile_picture_placeholder',
            apiField: { 
                fileUpload: 'profile_picture_file', 
                fileName: 'profile_picture_filename',
                imageDeleted: 'profile_picture_deleted'
            },
            cropConfig: {
                aspectRatio: 1,
                cropShape: 'round',
                resize: { width: 200, height: 200 }
            }
        },
        {
            name: 'external_code',
            label: 'user.general.external_code_label',
            type: 'text',
            placeholder: 'user.general.external_code_placeholder',
            tooltipText: 'user.general.external_code_tooltipText'
        },
        {
            name: 'is_active',
            label: 'user.general.is_active_label',
            type: 'checkbox',
            showOnEdit: true,
            tooltipText: 'user.general.is_active_tooltipText',
        },
    ];
    
    return (
        <FormModal 
            viewKey={viewKey}
            apiObject={apiObject}
            fields={fields}
            extraLeftButtons={itemId ? (
                <DropdownButton
                    icon={<FontAwesomeIcon icon={faEllipsis} />}
                    tooltipText='general.other'
                    items={[{ label: 'subcontractor.conversion.to_user_button', onClick: () => convertSubcontractorToUser(itemId) }]}
                    size='small'
                    menuDirection='up'
                    onlyViewRestriction={true}
                />
            ) : undefined}
        />
    );
};

export default SubcontractorForm;
